import { Episode, SeriesType } from '../types/index';
import { getAudioUrl, getPdfUrl } from '../config/audio';
import { getTranscriptUrl } from './mediaUtils';
import seriesMetadata from '../locales/series-metadata.json';

interface EpisodeContent {
  title: string;
  logline: string;
  episodeCard: string;
  summary: string;
}

interface SeriesContent {
  seriesTitle: string;
  seriesDescription: string;
  episodes: Record<string, EpisodeContent>;
}

interface EpisodeMetadata {
  id: number;
  summaryKey?: string;
  paperNumber?: number;
}

interface SeriesMetadata {
  seriesId: string;
  episodes: EpisodeMetadata[];
}

type ContentCollection = Record<string, SeriesContent>;
type ContentModule = { default: ContentCollection };

const contentLoaders = import.meta.glob<ContentModule>('../locales/*/content/content.json');
const prerenderContentModules = import.meta.env.SSR
  ? (import.meta.glob('../locales/*/content/content.json', {
      eager: true,
      import: 'default',
    }) as Record<string, ContentCollection>)
  : null;
const contentCache = new Map<string, Promise<ContentCollection>>();

function getSeriesMetadata(seriesId: string): SeriesMetadata | undefined {
  return seriesMetadata[seriesId as keyof typeof seriesMetadata] as SeriesMetadata | undefined;
}

function getContentLoader(language: string) {
  return contentLoaders[`../locales/${language}/content/content.json`];
}

async function loadContent(language: string): Promise<ContentCollection> {
  const normalizedLanguage = language || 'en';

  if (!contentCache.has(normalizedLanguage)) {
    const loader = getContentLoader(normalizedLanguage) ?? getContentLoader('en');

    if (!loader) {
      throw new Error(`Missing content loader for language: ${normalizedLanguage}`);
    }

    contentCache.set(
      normalizedLanguage,
      loader().then((module) => module.default),
    );
  }

  return contentCache.get(normalizedLanguage)!;
}

function loadContentSync(language: string): ContentCollection | null {
  if (!prerenderContentModules) {
    return null;
  }

  const normalizedLanguage = language || 'en';
  return (
    prerenderContentModules[`../locales/${normalizedLanguage}/content/content.json`] ||
    prerenderContentModules['../locales/en/content/content.json'] ||
    null
  );
}

async function getEpisodeContent(
  seriesId: string,
  episodeId: number,
  language: string,
): Promise<EpisodeContent | null> {
  const content = await loadContent(language);
  const seriesContent = content[seriesId];

  if (!seriesContent) {
    console.warn(`[episodeUtils] Series ${seriesId} not found in ${language} content`);
    return null;
  }

  const episodeContent = seriesContent.episodes[episodeId.toString()];

  if (!episodeContent) {
    console.warn(`[episodeUtils] Episode ${episodeId} not found in series ${seriesId} for ${language}`);
    return null;
  }

  return episodeContent;
}

function getEpisodeContentSync(
  seriesId: string,
  episodeId: number,
  language: string,
): EpisodeContent | null {
  const content = loadContentSync(language);
  const seriesContent = content?.[seriesId];

  if (!seriesContent) {
    return null;
  }

  return seriesContent.episodes[episodeId.toString()] || null;
}

function getImageUrl(seriesId: string, episodeId: number): string {
  return `/images/${seriesId}/card-${episodeId}.jpg`;
}

function buildSourceUrl(seriesId: string, summaryKey?: string): string | undefined {
  if (!summaryKey || !seriesId.startsWith('jesus-')) {
    return undefined;
  }

  return `https://discoverjesus.com/${summaryKey}`;
}

export async function getEpisode(
  seriesId: string,
  episodeId: number,
  language: string = 'en',
): Promise<Episode | undefined> {
  console.log(`[episodeUtils] Getting episode: ${seriesId}/${episodeId} (${language})`);

  const seriesData = getSeriesMetadata(seriesId);
  if (!seriesData) {
    console.warn(`[episodeUtils] Series ${seriesId} not found in metadata`);
    return undefined;
  }

  const episodeMetadata = seriesData.episodes.find((ep) => ep.id === episodeId);
  if (!episodeMetadata) {
    console.warn(`[episodeUtils] Episode ${episodeId} not found in series ${seriesId}`);
    return undefined;
  }

  const [enEpisodeContent, targetEpisodeContent] = await Promise.all([
    getEpisodeContent(seriesId, episodeId, 'en'),
    language === 'en' ? Promise.resolve(null) : getEpisodeContent(seriesId, episodeId, language),
  ]);

  if (!enEpisodeContent) {
    console.warn(`[episodeUtils] English content not found for ${seriesId}/${episodeId}`);
    return undefined;
  }

  const primaryContent = targetEpisodeContent || enEpisodeContent;
  const fallbackContent = enEpisodeContent;
  const sourceUrl = buildSourceUrl(seriesId, episodeMetadata.summaryKey);

  const episode: Episode = {
    id: episodeId,
    title: primaryContent.title || fallbackContent.title || `Episode ${episodeId}`,
    audioUrl: getAudioUrl(seriesId, episodeId, language),
    pdfUrl: getPdfUrl(seriesId, episodeId, language),
    imageUrl: getImageUrl(seriesId, episodeId),
    transcriptUrl: getTranscriptUrl(seriesId, episodeId, language) || undefined,
    sourceUrl,
    series: seriesId as SeriesType,
    description: primaryContent.logline || fallbackContent.logline || '',
    summary: primaryContent.summary || fallbackContent.summary || '',
    cardSummary:
      primaryContent.episodeCard ||
      fallbackContent.episodeCard ||
      primaryContent.logline ||
      fallbackContent.logline ||
      '',
    summaryKey: episodeMetadata.summaryKey || undefined,
  };

  console.log(`[episodeUtils] Built episode: ${episode.title}`);
  return episode;
}

export function getEpisodeSync(
  seriesId: string,
  episodeId: number,
  language: string = 'en',
): Episode | undefined {
  const seriesData = getSeriesMetadata(seriesId);
  if (!seriesData) {
    return undefined;
  }

  const episodeMetadata = seriesData.episodes.find((ep) => ep.id === episodeId);
  if (!episodeMetadata) {
    return undefined;
  }

  const enEpisodeContent = getEpisodeContentSync(seriesId, episodeId, 'en');
  const targetEpisodeContent = language === 'en' ? null : getEpisodeContentSync(seriesId, episodeId, language);

  if (!enEpisodeContent) {
    return undefined;
  }

  const primaryContent = targetEpisodeContent || enEpisodeContent;
  const fallbackContent = enEpisodeContent;
  const sourceUrl = buildSourceUrl(seriesId, episodeMetadata.summaryKey);

  return {
    id: episodeId,
    title: primaryContent.title || fallbackContent.title || `Episode ${episodeId}`,
    audioUrl: getAudioUrl(seriesId, episodeId, language),
    pdfUrl: getPdfUrl(seriesId, episodeId, language),
    imageUrl: getImageUrl(seriesId, episodeId),
    transcriptUrl: getTranscriptUrl(seriesId, episodeId, language) || undefined,
    sourceUrl,
    series: seriesId as SeriesType,
    description: primaryContent.logline || fallbackContent.logline || '',
    summary: primaryContent.summary || fallbackContent.summary || '',
    cardSummary:
      primaryContent.episodeCard ||
      fallbackContent.episodeCard ||
      primaryContent.logline ||
      fallbackContent.logline ||
      '',
    summaryKey: episodeMetadata.summaryKey || undefined,
  };
}

export async function getEpisodesForSeries(
  seriesId: string,
  language: string = 'en',
): Promise<Episode[]> {
  console.log(`[episodeUtils] Getting episodes for series: ${seriesId} (${language})`);

  const seriesData = getSeriesMetadata(seriesId);
  if (!seriesData) {
    console.warn(`[episodeUtils] Series ${seriesId} not found in metadata`);
    return [];
  }

  const episodes = (
    await Promise.all(
      seriesData.episodes.map((episodeMetadata) => getEpisode(seriesId, episodeMetadata.id, language)),
    )
  ).filter((episode): episode is Episode => Boolean(episode));

  console.log(`[episodeUtils] Found ${episodes.length} episodes for series ${seriesId}`);
  return episodes;
}

export function getEpisodesForSeriesSync(
  seriesId: string,
  language: string = 'en',
): Episode[] {
  const seriesData = getSeriesMetadata(seriesId);
  if (!seriesData) {
    return [];
  }

  return seriesData.episodes
    .map((episodeMetadata) => getEpisodeSync(seriesId, episodeMetadata.id, language))
    .filter((episode): episode is Episode => Boolean(episode));
}

export async function getRecentEpisodes(count: number = 5): Promise<Episode[]> {
  const allEpisodes = (
    await Promise.all(
      Object.keys(seriesMetadata).map((seriesId) => getEpisodesForSeries(seriesId, 'en')),
    )
  ).flat();

  allEpisodes.sort((a, b) => {
    if (a.series !== b.series) {
      return a.series.localeCompare(b.series);
    }
    return a.id - b.id;
  });

  return allEpisodes.slice(-count);
}

export async function getEpisodeById(
  id: number,
  series: string,
  language: string = 'en',
): Promise<Episode | undefined> {
  return getEpisode(series, id, language);
}

export function hasEpisodeInSeries(seriesId: string, episodeId: number): boolean {
  const seriesData = getSeriesMetadata(seriesId);
  return seriesData?.episodes.some((episode) => episode.id === episodeId) ?? false;
}

export function getUrantiaPaperPart(paperId: number): number {
  if (paperId <= 31) return 1;
  if (paperId <= 57) return 2;
  if (paperId <= 119) return 3;
  return 4;
}

export function generateAudioFileList(seriesId: string): string[] {
  const seriesData = getSeriesMetadata(seriesId);
  if (!seriesData) return [];

  return seriesData.episodes.map((episode) => {
    const audioUrl = getAudioUrl(seriesId, episode.id, 'en');
    return audioUrl.split('/').pop() || '';
  });
}

export function getDiscoverJesusSummary(sourceUrl: string | undefined): { cardSummary?: string; summary?: string } {
  if (!sourceUrl) {
    return {
      cardSummary: 'Read the full article on DiscoverJesus.com',
      summary: 'Read the full article on DiscoverJesus.com',
    };
  }

  return {
    cardSummary: 'Read the full article on DiscoverJesus.com',
    summary: 'Read the full article on DiscoverJesus.com',
  };
}
